import React, { useMemo } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Button, Skeleton, Tabs, Typography } from "antd";
import {
    ArrowLeftOutlined,
    LineChartOutlined,
    PartitionOutlined,
} from "@ant-design/icons";
import { useIntl } from "react-intl";
import { BrandAnalyticsComponent } from "../../components/BrandAnalytics";
import { SegmentComparisonTab } from "../../components/SegmentComparison/SegmentComparisonTab";
import { LockedSegmentComparisonPreview } from "../../components/SegmentComparison/LockedSegmentComparisonPreview";
import { useAuth } from "../../hooks/useAuth";
import {
    useGetOrganisationEntitlementsQuery,
} from "../../services/nestjs-base-api";

const { Title, Text } = Typography;

const SEGMENT_COMPARISON_ACCESS = "SEGMENT_COMPARISON_ACCESS";
const SEGMENT_COMPARISON_MAX_SERIES = "SEGMENT_COMPARISON_MAX_SERIES";

export const AdminBrandAnalyticsPage: React.FC = () => {
    const intl = useIntl();
    const { brandHash } = useParams<{ brandHash: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const jwtToken = user?.jwtToken ?? "";
    const orgId = user?.organisation?.id;

    const brandName = searchParams.get("brandName") || "Unknown Brand";
    const activeTab = searchParams.get("tab") === "compare" ? "compare" : "overview";

    const { data: entitlements, isLoading: entitlementsLoading } = useGetOrganisationEntitlementsQuery(
        { jwtToken, orgId: String(orgId ?? "") },
        { skip: !jwtToken || !orgId },
    );

    const { canCompareOnTrends, maxSeries } = useMemo(() => {
        const ent = entitlements ?? [];
        const access = ent.find((e) => e.feature === SEGMENT_COMPARISON_ACCESS);
        const maxRow = ent.find((e) => e.feature === SEGMENT_COMPARISON_MAX_SERIES);
        return {
            canCompareOnTrends: !!access?.hasAccess,
            maxSeries: maxRow?.limit ?? 0,
        };
    }, [entitlements]);

    // While the entitlements query is in-flight we don't know whether to show
    // the real tab or the locked preview. Render a skeleton instead of
    // defaulting to "locked" (which flashes the upgrade card for users who
    // are actually entitled).
    const renderCompareTabContent = () => {
        if (entitlementsLoading || !entitlements) {
            return (
                <div style={{ padding: 32 }}>
                    <Skeleton active paragraph={{ rows: 8 }} />
                </div>
            );
        }
        if (canCompareOnTrends) {
            return (
                <SegmentComparisonTab
                    brandHash={brandHash!}
                    brandName={brandName}
                    maxSeries={Math.max(maxSeries, 1)}
                />
            );
        }
        return <LockedSegmentComparisonPreview />;
    };

    const handleBackClick = () => {
        navigate("/admin/trends");
    };

    const handleTabChange = (key: string) => {
        const next = new URLSearchParams(searchParams);
        if (key === "compare") next.set("tab", "compare");
        else next.delete("tab");
        setSearchParams(next, { replace: true });
    };

    if (!brandHash) {
        return (
            <div className="brand-analytics-container">
                <div className="brand-analytics-error">
                    <Title level={3} className="brand-analytics-error-title">
                        Invalid Brand
                    </Title>
                    <p className="brand-analytics-error-message">
                        No brand specified for analytics. Please select a brand from the analytics dashboard.
                    </p>
                    <Button type="primary" onClick={handleBackClick}>
                        Back to Trends Dashboard
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div style={{ position: "relative" }}>
            {/* Navigation Header */}
            <div
                style={{
                    padding: "16px 24px",
                    background: "var(--card-background)",
                    borderBottom: "1px solid var(--border-color)",
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                }}
            >
                <Button icon={<ArrowLeftOutlined />} onClick={handleBackClick} type="text">
                    Back to Trends
                </Button>
                <div
                    style={{
                        borderLeft: "1px solid var(--border-color)",
                        height: "24px",
                        margin: "0 8px",
                    }}
                />
                <Text strong style={{ fontSize: "20px" }}>
                    {brandName}
                </Text>
            </div>

            <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
                style={{ padding: "0 32px" }}
                items={[
                    {
                        key: "overview",
                        label: (
                            <span>
                                <LineChartOutlined style={{ marginRight: 8 }} />
                                {intl.formatMessage({
                                    id: "trends.tabs.overview",
                                    defaultMessage: "Overview",
                                })}
                            </span>
                        ),
                        children: (
                            <BrandAnalyticsComponent
                                brandHash={brandHash}
                                brandName={brandName}
                            />
                        ),
                    },
                    {
                        key: "compare",
                        label: (
                            <span>
                                <PartitionOutlined style={{ marginRight: 8 }} />
                                {intl.formatMessage({
                                    id: "trends.tabs.segmentComparison",
                                    defaultMessage: "Segment Comparison",
                                })}
                                {!entitlementsLoading && entitlements && !canCompareOnTrends && (
                                    <span
                                        style={{
                                            marginLeft: 8,
                                            fontSize: 10,
                                            padding: "1px 6px",
                                            background: "rgba(255,130,92,0.12)",
                                            color: "#ff825c",
                                            borderRadius: 8,
                                            fontWeight: 600,
                                            letterSpacing: 0.4,
                                        }}
                                    >
                                        PRO
                                    </span>
                                )}
                            </span>
                        ),
                        children: renderCompareTabContent(),
                    },
                ]}
            />
        </div>
    );
};

export default AdminBrandAnalyticsPage;
